import { isEmpty, isNil } from 'lodash';
import React from 'react';
import { MultiValueProps, SingleValueProps } from 'react-select';
import { View } from '@shared/components/View';
import {
  FilterOperation,
  FilterOption,
  FilterSelection,
  FilterSelectionState,
  FilterTagOption,
} from '@shared/components/filter/types';
import { Pill } from '@shared/components/pill';
import { MBTextBody1 } from '@shared/components/typography';
import { isTagComponent } from '@shared/scenes/workflows/utils';
import { grid } from '@shared/styles';
import { i18n } from '@shared/util/i18n';
import { OpenView } from './multiSelect/MultiSelect';
import { OptionProps, Option as SelectOption } from './multiSelect/Option';
import { Selection } from './multiSelect/Selection';

function Option({ children, ...props }: OptionProps) {
  const data = props.data as FilterTagOption;
  const isEmptyFilter = data.value === 'empty';

  return (
    <SelectOption {...props} icon="tag">
      {isEmptyFilter && (
        <MBTextBody1 style={{ marginLeft: grid(1) }}>{data.label ?? ''}</MBTextBody1>
      )}
      {!isEmptyFilter && (
        <Pill
          backgroundColor={data.archived ? undefined : data.backgroundColor}
          round
          sections={[data.label ?? '']}
          maxWidth={215}
          italic={data.archived}
        />
      )}
    </SelectOption>
  );
}

function MultiValue({ children, ...props }: MultiValueProps<FilterTagOption>) {
  const data = props.data as FilterTagOption;

  return (
    <Selection {...props}>
      <Pill
        backgroundColor={data.archived ? undefined : data.backgroundColor}
        round
        sections={[data.label ?? i18n('card.actions.groups.missingTagLabel')]}
        maxWidth={grid(24)}
        italic={data.archived || isNil(data.label)}
        onRemove={() =>
          props.setValue(
            [...(props.getValue() as FilterTagOption[]).filter(v => v.value !== data.value)],
            'deselect-option',
          )
        }
      />
    </Selection>
  );
}

function SingleValue({ children, ...props }: SingleValueProps<FilterTagOption>) {
  const data = props.data as FilterTagOption;

  return (
    <View style={{ alignItems: 'flex-start' }}>
      <Pill
        backgroundColor={data.archived ? undefined : data.backgroundColor}
        round
        sections={[data.label ?? i18n('card.actions.groups.missingTagLabel')]}
        maxWidth={grid(24)}
        italic={data.archived || isNil(data.label)}
        onRemove={() =>
          props.setValue(
            [...(props.getValue() as FilterTagOption[]).filter(v => v.value !== data.value)],
            'deselect-option',
          )
        }
      />
    </View>
  );
}

export function TagOptions({
  filter,
  setOption,
  selectedOptions: selectedOptionsMap,
  includeEmptyOption,
  isMultiSelect = true,
}: {
  filter: FilterOption;
  setOption: (
    filter: FilterOption,
    operation: FilterOperation,
    selection?: FilterSelection,
    negate?: boolean,
  ) => void;
  selectedOptions: Map<string, FilterSelectionState>;
  includeEmptyOption: boolean;
  isMultiSelect?: boolean;
}) {
  const fullFilter = selectedOptionsMap.get(filter.uuid ?? '');
  const negate = fullFilter?.negate;

  const onChange = selected => {
    // When clearing selections, selected is an empty array - don't wrap it to avoid [[]]
    const selection = isMultiSelect || isEmpty(selected) ? selected : [selected];
    setOption(filter, 'updateSelections', selection, negate);
  };

  if (!filter.component || !isTagComponent(filter.component)) {
    return null;
  }

  const tagComponent = filter.component;
  const rawSelections = filter.uuid ? selectedOptionsMap.get(filter.uuid)?.selection ?? [] : [];

  const selectionArray = Array.isArray(rawSelections) ? rawSelections : [];

  const emptyLabel = i18n('card.actions.filters.emptyLabel', { label: filter.label ?? '' });

  const selectedOptions: FilterTagOption[] = selectionArray.map(sel =>
    toFilterTagOption(
      sel,
      (tagComponent.options ?? []) as unknown as FilterTagOption[],
      emptyLabel,
    ),
  );

  return (
    <OpenView
      componentId={filter.id}
      options={[
        ...(includeEmptyOption
          ? [
              {
                value: 'empty',
                label: i18n('card.actions.filters.emptyLabel', { label: filter.label ?? '' }),
              },
            ]
          : []),
        ...((tagComponent.options?.filter(o => !o.archived) ?? []).map(o => ({
          ...o,
          label: o.label ?? '',
        })) as FilterTagOption[]),
        ...((tagComponent.options?.filter(o => o.archived) ?? []).map(o => ({
          ...o,
          label: o.label ?? '',
        })) as FilterTagOption[]),
      ]}
      onChange={onChange}
      selectedOptions={selectedOptions}
      components={{ Option, MultiValue, SingleValue }}
      isMultiSelect={isMultiSelect}
    />
  );
}

// Basic shape we care about from whatever ends up in `filter.selection`.
type RawSelection = {
  value: 'empty' | string | number;
  label?: string;
  backgroundColor?: FilterTagOption['backgroundColor'];
  archived?: boolean;
};

/**
 * Convert a raw selection value coming from filter state into a FilterTagOption that the
 * react-select components can render.
 */
function toFilterTagOption(
  selection: RawSelection,
  availableOptions: FilterTagOption[],
  emptyLabel: string,
): FilterTagOption {
  // Handle the special "empty" placeholder option first.
  if (selection.value === 'empty') {
    return { value: 'empty', label: emptyLabel };
  }

  // Attempt to find a matching canonical option from the component's configuration.
  const match = availableOptions.find(
    option => typeof selection.value === 'string' && option.value === selection.value,
  );

  if (match) {
    // Ensure the label is never undefined so the UI always has text to display.
    return { ...match, label: match.label ?? '' };
  }

  // Fallback for archived or otherwise unknown tags that may still be selected.
  const backgroundColor = selection.backgroundColor ?? undefined;

  return {
    value: selection.value,
    label: selection.label ?? '',
    backgroundColor,
    archived: selection.archived,
  };
}

// --- Helper functions ------------------------------------------------------
